import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
  getBlockSchedules, createAlternatePlan, updateAlternatePlan,
} from "../../lib/api";
import AltPlanFields from "./AltPlanFields";
import ScheduleReasonFields, { EMPTY_REASON } from "./ScheduleReasonFields";

const EMPTY_ALT_FORM = {
  plan_type: "weekly", days: [], dates: [], start_date: "", end_date: "",
  arrival_time: "", departure_time: "", destination: "", notes: "", full_day_out: false, ...EMPTY_REASON,
};

function seedFromPlan(plan) {
  return {
    plan_type: ["dates", "range"].includes(plan.plan_type) ? plan.plan_type : "weekly",
    days: plan.days || [], dates: plan.dates || [], start_date: plan.start_date || "", end_date: plan.end_date || "",
    arrival_time: plan.arrival_time || "", departure_time: plan.departure_time || plan.effective_departure || "",
    destination: plan.destination, notes: plan.notes || "", full_day_out: !!plan.full_day_out, ...EMPTY_REASON,
  };
}

/**
 * The Add/Edit Alternate Plan form body (schedule type + times + destination +
 * reason fields). Shared by the student-file Schedule tab and the school-wide
 * Schedule Changes page. Renders the inner content only — wrap it in a Dialog.
 * Calls create/update alternate-plan endpoints, so logging stays identical.
 */
export default function AlternatePlanForm({ studentId, staff = [], editingPlan = null, onSaved, onCancel }) {
  const [blocks, setBlocks] = useState([]);
  const [form, setForm] = useState(() => (editingPlan ? seedFromPlan(editingPlan) : { ...EMPTY_ALT_FORM }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getBlockSchedules().then((res) => setBlocks(res.data?.full_day?.blocks || [])).catch(() => setBlocks([]));
  }, []);

  const handleSave = async () => {
    const isDates = form.plan_type === "dates";
    const isRange = form.plan_type === "range";
    const hasWindow = form.arrival_time || form.departure_time;
    const fullOut = !!form.full_day_out;
    const scheduleMissing = isRange ? (!form.start_date || !form.end_date) : isDates ? !form.dates.length : !form.days.length;
    if (scheduleMissing || (!fullOut && !hasWindow) || !form.destination) {
      toast.error("Please fill in the schedule, a destination, and an arrival/departure time (unless full day out)");
      return;
    }
    if (!form.reason_category) { toast.error("Please choose a reason for this plan"); return; }
    if (isRange && form.start_date && form.end_date && form.end_date < form.start_date) {
      toast.error("End date must be on or after the start date"); return;
    }
    if (!fullOut && form.arrival_time && form.departure_time && form.departure_time <= form.arrival_time) {
      toast.error("Departure time must be after arrival time"); return;
    }
    setSaving(true);
    try {
      if (editingPlan) {
        await updateAlternatePlan(editingPlan.id, form);
        toast.success("Alternate plan updated");
      } else {
        await createAlternatePlan({ ...form, student_id: studentId });
        toast.success("Alternate plan created");
      }
      onSaved && onSaved();
    } catch {
      toast.error("Failed to save alternate plan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pt-2 space-y-4">
      <AltPlanFields form={form} setForm={setForm} blocks={blocks} testidPrefix="alt-" />
      <ScheduleReasonFields form={form} setForm={setForm} staff={staff} testidPrefix="alt-" />
      <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={handleSave} disabled={saving} data-testid="save-alt-plan-btn">
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}
